import React, { useEffect, useState } from 'react';
import Drawer from '../../Common/Drawer';
import { useToast } from '../../Common/ToastProvider';
import { useConfirm } from '../../Common/ConfirmDialog';
import {
  patchWorkItem, takeOverWorkItem, startWorkItem, closeWorkItem,
  addComment, deleteWorkItem, logHours,
  uploadAttachment, attachmentDownloadUrl, deleteAttachment,
  readServerError,
  // QLOG-S2 — quickLogMode resolves the auto-holder for a leaf costed
  // task before it opens the hours block; the same endpoint QLOG-S1
  // shipped is reused here as-is.
  quickLogAnchor, getWorkItemContext,
} from '../../../api/deliveryApi';
import RichTextField from './RichTextField';
import DrawerTaskTree from './DrawerTaskTree';
import TaskDrawerForm from './TaskDrawerForm';
import CreateWorkItemForm from './CreateWorkItemForm';
import Discussion from './Discussion';
import FieldEditingIndicator from './FieldEditingIndicator';
import * as live from '../../../api/liveClient';
// WI-S3.1 (BLOCKER-1) — local-calendar window helper. Extracted so a
// pure Jest unit test can pin the ISO boundary without dragging the
// axios/MSAL import chain of this file.
import { isInHoursWindow, isoOf, firstOfMonth, startOfLocalToday } from './hoursWindow';
export { isInHoursWindow };

// DELIVERY-WI (WI-S1): drawer for a single work item.
// Sections: header (WI-{id}, State pill, editable title, action button,
// assignee select + take-over), Deliverable (What / Accept when),
// Planning (Start/Finish, read-only note for parents), Discussion
// (comments + new comment), footer (Delete for owner, Close dialog).
// v1 has NO Log hours (S2) and NO Attachments (S3).
//
// Rights (rendered, not disabled — no dead controls):
//   canEdit    = caller is task owner OR PM AND not Closed.
//   canAct     = caller is assignee OR task owner OR PM.
//   canTakeOver= reader AND WI has no assignee AND not Closed.
//   canDelete  = task owner OR PM.

const isoDate = (v) => {
  if (!v) return '';
  const d = v instanceof Date ? v : new Date(v);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('sv-SE');
};

// WI-S3.1 (MAJOR-4) — server error mapping lives in deliveryApi.js.

// WI-S3.2 (A) — flattens the server's [{date, hours}] list into
// { 'YYYY-MM-DD': hours } for the drawer's local day map.
const seedHoursMap = (list) => {
  const m = {};
  if (!Array.isArray(list)) return m;
  for (const e of list) {
    const iso = e?.date ? isoOf(new Date(e.date)) : '';
    if (iso) m[iso] = Number(e.hours) || 0;
  }
  return m;
};

const HOURS_STEP = 0.5;
const PRESETS = [2, 4, 6, 8];
const clampQuarter = (n) => Math.max(0, Math.round(n / 0.25) * 0.25);
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const isSameDay = (a, b) => a && b && isoOf(a) === isoOf(b);
const monthKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
const dayLabel = (d) => d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });

const WorkItemDrawer = ({
  open, onClose, wi, task, team, caller, onChanged, onWorkItemUpdated,
  hasChildren = false,
  // UDRAW-S1 — DeliveryPage builds the phase-scoped sub-tree for the
  // currently open WI and hands it in, plus a callback that routes a
  // click back through its normal row-click flow (WI drawer / info
  // drawer / quickLog drawer). If either prop is omitted the drawer
  // just skips the tree render.
  treeData = null,
  onTreeNavigate = null,
  // TC-S3 — read-only audit view opened from the Team Calendar drill.
  // When true, the drawer renders exclusively from the `audit` payload
  // and skips every delivery-path effect / call. Normal-mode markup
  // and behaviour are untouched by design.
  auditMode = false,
  audit = null,
  // QLOG-S2 — read-only "info" mode for non-writable delivery rows
  // (WBS / summary task / task-with-WIs / WI-with-children / milestone).
  // Uses the same visual shell as the normal drawer; renders exclusively
  // from `infoContent` (no delivery-path effects / API calls).
  infoMode = false,
  infoContent = null,
  // QLOG-S2 — "quick log" mode for a LEAF costed task with no WIs.
  // Resolves the QLOG-S1 auto-holder (task × role × employee) on mount /
  // role change, then renders the hours block against the resolved WI.
  // Signature: { projectId, projectName, task, team, caller, onRefreshBoard? }.
  quickLogMode = false,
  quickLogContext = null,
  // UDRAW-S3 §2.2 — "+ New child work item" toolbelt entry. Hidden
  // when the anchor WI is Closed or is already at depth 3 (max nesting).
  // Also used by the auto-open flow so the "+ child" grid button lands
  // directly on the create form.
  canHaveChildren = true,
  autoOpenCreate = false,
  onWorkItemCreated = null,
  isBillable = true,
  // UDRAW-S3.1 HOTFIX — the WI DTO does NOT carry ProjectId
  // (DeliveryWorkItemDto has only ProjectTaskId), so the child-create
  // POST was hitting `/api/projects/undefined/work-items` and 404-ing.
  // DeliveryPage now passes projectId explicitly on both drawer
  // instances; CreateWorkItemForm reads it from here.
  projectId = null,
  // UDRAW-S5.1 §2.2.4 — DeliveryPage passes the fieldEditing map
  // filtered to THIS WI's anchor (wi-{id}); keys are the raw
  // fieldKey (e.g. 'deliverable', 'acceptance', 'discussion').
  // Values are { employeeId, fullName }.
  liveEditing = null,
  // UDRAW-S5-A7 — the item this drawer opened on was deleted by a
  // peer while the drawer was still open (WI/task disappeared from
  // the DeliveryView on the last `deliveryChanged`). Renders a non-
  // blocking notice inside the drawer chrome; the viewer decides
  // when to close. Applies to every mode (normal / info / quickLog).
  missing = false,
}) => {
  const toast = useToast();
  const confirm = useConfirm();

  // WI-S3.1 (MAJOR-9) — every editable field carries a `dirty` flag.
  // When the parent refetches and hands us a new `wi` (same id, new
  // reference) the useEffect below re-syncs from the server ONLY for
  // fields the user hasn't touched. What / Accept when / Title /
  // Planning drafts survive a save race between fields — the field
  // that lost focus flushes to the server, the field the user just
  // moved into keeps its typed value until it too blurs.
  const [title, setTitle] = useState(wi?.title || '');
  const [start, setStart] = useState(isoDate(wi?.plannedStart));
  const [finish, setFinish] = useState(isoDate(wi?.plannedFinish));
  const [deliverable, setDeliverable] = useState(wi?.deliverable || '');
  const [accept, setAccept] = useState(wi?.acceptanceCriteria || '');
  const [assigneeId, setAssigneeId] = useState(wi?.assigneeEmployeeId ?? '');
  const [dirty, setDirty] = useState({
    title: false, start: false, finish: false, deliverable: false, accept: false,
  });
  const clearDirty = (k) => setDirty((d) => ({ ...d, [k]: false }));
  const markDirty = (k) => setDirty((d) => d[k] ? d : ({ ...d, [k]: true }));
  // UDRAW-S4 §2.2 — the shared <Discussion/> now owns editor state.
  const [showZeroConfirm, setShowZeroConfirm] = useState(false);
  const [saving, setSaving] = useState(false);
  // UDRAW-S3 §2.2 — "+ New child work item" body swap. `justCreated`
  // powers the green "Created WI-X — Open →" banner shown once the
  // form closes.
  const [createOpen, setCreateOpen] = useState(false);
  const [justCreated, setJustCreated] = useState(null);
  useEffect(() => {
    if (autoOpenCreate) setCreateOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wi?.id]);
  // QLOG-S2.2 — the Log hours block (state + navigator + stepper +
  // calendars + postHours) has been extracted to the <HoursBlock/>
  // component at file bottom so quickLogMode reuses IT (not a parallel
  // mini-input). This drawer keeps only the enclosing chrome; the
  // former zoom / pickedDay / hoursForDay / … hooks now live inside
  // HoursBlock.
  //
  // WI-S3 — attachment upload state (must be declared before the
  // early return so hook order stays stable).
  const [uploading, setUploading] = useState(false);

  // WI-S3.1 (MAJOR-9) — reset drawer state fully only when the WI
  // ID changes (open a different work item). Same-id refreshes are
  // handled by the effect BELOW so unsaved drafts survive.
  // WI-S3.2 (A) — seed the day-map from wi.hoursByDate (server ships
  // the current-month per-day map + prev-months total).
  useEffect(() => {
    setTitle(wi?.title || '');
    setStart(isoDate(wi?.plannedStart));
    setFinish(isoDate(wi?.plannedFinish));
    setDeliverable(wi?.deliverable || '');
    setAccept(wi?.acceptanceCriteria || '');
    setAssigneeId(wi?.assigneeEmployeeId ?? '');
    setDirty({ title: false, start: false, finish: false, deliverable: false, accept: false });
    setShowZeroConfirm(false);
    // QLOG-S2.2 — hours-block state (hoursForDay / pickedDay / zoom /
    // showWeekend / dayInput) is now owned by HoursBlock and reset on
    // its own wi.id-change effect. Nothing to reset here.
  }, [wi?.id]);

  // Same-id refresh: re-sync ONLY non-dirty fields so the user's
  // unsaved draft in one field can't be blown away by a parent
  // reload triggered by another field's save.
  useEffect(() => {
    if (!wi) return;
    if (!dirty.title && (wi.title || '') !== title) setTitle(wi.title || '');
    if (!dirty.start && isoDate(wi.plannedStart) !== start) setStart(isoDate(wi.plannedStart));
    if (!dirty.finish && isoDate(wi.plannedFinish) !== finish) setFinish(isoDate(wi.plannedFinish));
    if (!dirty.deliverable && (wi.deliverable || '') !== deliverable) setDeliverable(wi.deliverable || '');
    if (!dirty.accept && (wi.acceptanceCriteria || '') !== accept) setAccept(wi.acceptanceCriteria || '');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wi]);

  // TC-S3 — audit mode: read-only render from the `audit` prop.
  // Rendered as an early return BEFORE any normal-mode computation so
  // that delivery-path state (title/dirty/hours/…) stays inert. Zero
  // delivery API calls happen in this branch (the audit prop is the
  // sole data source).
  if (open && auditMode && audit) {
    return renderAuditDrawer({ open, onClose, audit });
  }
  // UDRAW-S5-A7 — the item vanished from the live view while this
  // drawer was open (peer deleted the WI / task). Render a non-
  // blocking notice; no crash, no auto-close. Precedes every other
  // branch so a missing `infoContent` / `quickLogContext` / `wi`
  // caused by the deletion resolves to this message instead of a
  // null render or a blank normal-mode form.
  if (open && missing) {
    return renderMissingDrawer({ open, onClose });
  }
  // QLOG-S2 — info mode: read-only summary render. Same pattern as
  // auditMode — the branch is inert for the normal-mode hooks above.
  if (open && infoMode && infoContent) {
    return renderInfoDrawer({ open, onClose, infoContent });
  }
  // QLOG-S2 — quick log mode: dispatch to a sub-component that owns
  // its own state (role select, resolved anchor, day hours). The
  // outer component's normal-mode state stays inert here.
  if (open && quickLogMode && quickLogContext) {
    return <QuickLogInner open={open} onClose={onClose} ctx={quickLogContext} onChanged={onChanged} />;
  }
  if (!open || !wi) return null;

  const isClosed = wi.status === 'Closed';
  const isOwnerOfTask = !!caller?.isPm || task?.ownerEmployeeId === caller?.employeeId;
  const isAssignee = wi.assigneeEmployeeId === caller?.employeeId;
  const canEdit = isOwnerOfTask && !isClosed;
  const canAct = (isAssignee || isOwnerOfTask) && !isClosed;
  const canTakeOver = !isClosed && wi.assigneeEmployeeId == null && !isAssignee;
  const canDelete = isOwnerOfTask;

  // WI-S3.1 (MAJOR-9) — patch returns the fresh WI DTO. We update
  // the parent's view in-place through onWorkItemUpdated instead of
  // triggering a full delivery re-fetch (which caused the What/Accept
  // race). fields param names which local dirty flag(s) to clear on
  // success. onChanged is kept as a fallback for callers without the
  // in-place update (unused in DeliveryPage; safety net).
  const doSave = async (patch, fields = []) => {
    setSaving(true);
    try {
      const updated = await patchWorkItem(wi.id, patch);
      for (const f of fields) clearDirty(f);
      if (updated && onWorkItemUpdated) onWorkItemUpdated(updated);
      else onChanged?.();
      toast('Plan updated.', { variant: 'success' });
    } catch (err) {
      toast(readServerError(err), { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const doSaveTitle = () => {
    if (!dirty.title || !title.trim() || title === wi.title) { clearDirty('title'); return; }
    doSave({ title }, ['title']);
  };
  const doSavePlanning = () => {
    const p = {};
    const fields = [];
    if (dirty.start && isoDate(wi.plannedStart) !== start) { p.plannedStart = start || null; fields.push('start'); }
    if (dirty.finish && isoDate(wi.plannedFinish) !== finish) { p.plannedFinish = finish || null; fields.push('finish'); }
    if (fields.length) doSave(p, fields);
    else { clearDirty('start'); clearDirty('finish'); }
  };
  // WI-S3.1 (MAJOR-9) — save exactly the field that lost focus,
  // NOT the other one. This prevents What's onBlur from also
  // sending Accept when's current (possibly stale) local state.
  const doSaveWhat = () => {
    if (!dirty.deliverable || (wi.deliverable || '') === deliverable) { clearDirty('deliverable'); return; }
    doSave({ deliverable }, ['deliverable']);
  };
  const doSaveAccept = () => {
    if (!dirty.accept || (wi.acceptanceCriteria || '') === accept) { clearDirty('accept'); return; }
    doSave({ acceptanceCriteria: accept }, ['accept']);
  };
  const doSaveAssignee = (val) => {
    const patch = val === '' ? { clearAssignee: true } : { assigneeEmployeeId: Number(val) };
    doSave(patch);
  };

  // RA-S2 — change the role the WI is charged against. Server-side
  // validation covers ROLE_NOT_ON_TASK and forbids clearing on
  // billable WIs, so the drawer just sends the requested id.
  const doSaveRole = (val) => {
    const asInt = Number(val);
    if (!Number.isFinite(asInt) || asInt <= 0) return;
    if (asInt === (wi.roleId || 0)) return;
    doSave({ projectRoleId: asInt });
  };

  const doTakeOver = async () => {
    try {
      await takeOverWorkItem(wi.id);
      toast("It's yours.", { variant: 'success' });
      onChanged?.();
    } catch (err) {
      toast(readServerError(err), { variant: 'error' });
    }
  };
  const doStart = async () => {
    try {
      await startWorkItem(wi.id);
      toast('Started.', { variant: 'success' });
      onChanged?.();
    } catch (err) {
      toast(readServerError(err), { variant: 'error' });
    }
  };
  const doCloseWith0 = async () => {
    try {
      await closeWorkItem(wi.id, true);
      toast('Closed. For returned work, create a new work item.', { variant: 'success' });
      setShowZeroConfirm(false);
      onChanged?.();
    } catch (err) {
      toast(readServerError(err), { variant: 'error' });
    }
  };
  const doDelete = async () => {
    const ok = await confirm({
      message: `Delete this work item (WI-${wi.id})?`,
      danger: true, confirmLabel: 'Delete',
    });
    if (!ok) return;
    try {
      await deleteWorkItem(wi.id);
      toast('Deleted.', { variant: 'success' });
      onChanged?.();
      onClose?.();
    } catch (err) {
      toast(readServerError(err), { variant: 'error' });
    }
  };
  // WI-S2 / WI-S3.1 — Log hours logic. Only the assignee can WRITE;
  // everyone else sees the same visualisation with disabled controls
  // (never bare text). Leaves only; parents show the aggregate.
  // QLOG-S2.2 — everything below (state, renderNavigator/Stepper/…,
  // postHours) has moved to <HoursBlock/>. The drawer just passes the
  // wi + rights into HoursBlock now.
  const isLeaf = !hasChildren;
  const canLogHours = !isClosed && isAssignee;
  // QLOG-S2.2 — hours navigator / stepper / calendars / postHours all
  // moved to <HoursBlock/> (bottom of file). The JSX below just mounts
  // it.

  // WI-S3 — attachments. Any reader can upload (even on Closed WIs);
  // delete requires uploader OR task owner OR PM AND the WI is open —
  // the server resolves canDelete per-caller and returns it in the DTO.
  // (useState declared above the early return — see top of component.)
  const doUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      await uploadAttachment(wi.id, file);
      toast('Attached.', { variant: 'success' });
      onChanged?.();
    } catch (err) {
      toast(readServerError(err), { variant: 'error' });
    } finally {
      setUploading(false);
    }
  };
  const doDeleteAttachment = async (att) => {
    const ok = await confirm({
      message: `Remove attachment “${att.fileName}”?`,
      danger: true, confirmLabel: 'Remove',
    });
    if (!ok) return;
    try {
      await deleteAttachment(wi.id, att.id);
      toast('Removed.', { variant: 'success' });
      onChanged?.();
    } catch (err) {
      toast(readServerError(err), { variant: 'error' });
    }
  };
  function renderAttachments() {
    const atts = wi.attachments || [];
    return (
      <div data-testid="wi-drawer-attachments">
        <h3>Attachments</h3>
        {atts.length === 0 && (
          <div style={{ color: '#999', fontSize: 12, marginBottom: 6 }}>No files attached yet.</div>
        )}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {atts.map((a) => (
            <span key={a.id}
                  data-testid={`wi-attachment-${a.id}`}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    background: '#f0f4ff', border: '1px solid #cdddf7',
                    borderRadius: 12, padding: '2px 10px', fontSize: 12,
                  }}>
              <a href={attachmentDownloadUrl(wi.id, a.id)}
                 data-testid={`wi-attachment-link-${a.id}`}
                 style={{ color: '#005a99', textDecoration: 'none' }}
                 rel="noopener noreferrer">📄 {a.fileName}</a>
              {a.canDelete && (
                <button
                  data-testid={`wi-attachment-delete-${a.id}`}
                  onClick={() => doDeleteAttachment(a)}
                  style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#a12a2a', fontSize: 12 }}>
                  ✕
                </button>
              )}
            </span>
          ))}
        </div>
        <div className="row" style={{ marginTop: 8 }}>
          <label htmlFor={`wi-att-input-${wi.id}`}
                 style={{ cursor: 'pointer', color: '#4a90d9', fontSize: 12 }}>
            📎 Attach a file
          </label>
          <input
            id={`wi-att-input-${wi.id}`}
            data-testid="wi-attachment-picker"
            type="file"
            disabled={uploading}
            onChange={(e) => { const f = e.target.files?.[0]; if (f) doUpload(f); e.target.value = ''; }}
            style={{ display: 'none' }}
          />
        </div>
      </div>
    );
  }

  // UDRAW-S4 §2.2 — the shared <Discussion/> owns the editor state
  // now; this posts and hands the fresh DTO back to the parent so the
  // list can grow without a full GET-delivery round-trip.
  const doAddComment = async (html) => {
    const res = await addComment(wi.id, html);
    if (typeof onWorkItemUpdated === 'function' && res && res.id != null) {
      const nextComments = [...(wi.comments || []), res];
      onWorkItemUpdated({ ...wi, comments: nextComments });
    } else {
      onChanged?.();
    }
  };

  // UDRAW-S1 — the old `actionButton()` helper that used to render
  // Start / Close / Take-over inside the Assignee row is retired. The
  // toolbelt below owns Start / Close; the strip's Assignee cell owns
  // the Take-over link.

  const miClass = wi.status === 'Closed' ? 'st-c' : wi.status === 'Active' ? 'st-i' : 'st-n';
  const miLetter = wi.status === 'Closed' ? 'C' : wi.status === 'Active' ? 'A' : 'N';

  // UDRAW-S1 — banner strings for the "has parent" and "has children"
  // rows in the left column.
  const parentWiId = wi.parentWorkItemId || null;
  const stateClass = wi.status === 'Closed' ? 'd-cls' : wi.status === 'Active' ? 'd-act' : 'd-new';
  const initials = (n) => (n ? n.split(' ').map((x) => x[0] || '').slice(0, 2).join('').toUpperCase() : '');
  const canWriteRichText = canEdit;

  const doSaveDescription = async (html) => {
    setDeliverable(html);
    await new Promise((resolve, reject) => {
      // Use the same patch flow as the old textarea onBlur — but bypass
      // dirty state since RichTextField manages the edit lifecycle.
      (async () => {
        try {
          const updated = await patchWorkItem(wi.id, { deliverable: html });
          if (updated && onWorkItemUpdated) onWorkItemUpdated(updated);
          else onChanged?.();
          toast('Plan updated.', { variant: 'success' });
          resolve();
        } catch (err) {
          toast(readServerError(err), { variant: 'error' });
          reject(err);
        }
      })();
    });
  };
  const doSaveAcceptance = async (html) => {
    setAccept(html);
    await new Promise((resolve, reject) => {
      (async () => {
        try {
          const updated = await patchWorkItem(wi.id, { acceptanceCriteria: html });
          if (updated && onWorkItemUpdated) onWorkItemUpdated(updated);
          else onChanged?.();
          toast('Plan updated.', { variant: 'success' });
          resolve();
        } catch (err) {
          toast(readServerError(err), { variant: 'error' });
          reject(err);
        }
      })();
    });
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width="xl"
      title={
        <span data-testid="wi-drawer-title-node" className="pdp"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <span style={{ color: '#4a90d9', fontWeight: 600, fontSize: 12 }}>WI-{wi.id}</span>
          <span className={`mi ${miClass}`}
                data-testid="wi-drawer-state-pill"
                title={wi.status}
                style={{ display: 'inline-block', width: 18, height: 18, borderRadius: 2,
                         lineHeight: '18px', fontSize: 9, fontWeight: 700,
                         textAlign: 'center' }}>
            {miLetter}
          </span>
        </span>
      }
      testId="wi-drawer"
    >
      <div className="pdp dlv-drawer" data-dlv-drawer style={{ padding: 12 }}>
        {/* UDRAW-S1 §1.1 — Title row (edge-to-edge, semibold). WI-S3.1
            MINOR-5: Closed renders as read-only text so there's no dead
            editable input. */}
        <div className="wi-drawer-title-row" data-testid="wi-drawer-title-row">
          {canEdit ? (
            <input
              type="text"
              className="wi-drawer-title-input"
              data-testid="wi-drawer-title-input"
              value={title}
              onChange={(e) => { setTitle(e.target.value); markDirty('title'); }}
              onBlur={doSaveTitle}
              disabled={saving}
            />
          ) : (
            <div className="wi-drawer-title-ro" data-testid="wi-drawer-title-ro">{wi.title}</div>
          )}
        </div>

        {/* UDRAW-S1 §1.2 — Strip: State chip · Assignee · Role. */}
        <div className="wi-drawer-strip" data-testid="wi-drawer-strip">
          <div className="cell">
            <div className="l">State</div>
            <div className="pv" data-testid="wi-drawer-strip-state">
              <span className={`dot ${stateClass}`} />
              <span>{wi.status}</span>
            </div>
          </div>
          <div className="cell">
            <div className="l">Assignee</div>
            {canEdit ? (
              <select
                data-testid="wi-drawer-assignee"
                value={assigneeId === '' ? '' : String(assigneeId)}
                onChange={(e) => { setAssigneeId(e.target.value); doSaveAssignee(e.target.value); }}
              >
                <option value="">— none —</option>
                {/* CEV-S3 — people who have left are not offered; a current former assignee stays visible */}
                {team.filter((m) => !m.isFormer || String(m.employeeId) === String(assigneeId)).map((m) => (
                  <option key={m.employeeId} value={m.employeeId} disabled={m.isFormer}>{m.fullName}{m.isFormer ? ' (left)' : ''}</option>
                ))}
              </select>
            ) : (
              <div className="pv" data-testid="wi-drawer-strip-assignee">
                {wi.assigneeFullName ? (
                  <>
                    <span className="avat">{initials(wi.assigneeFullName)}</span>
                    <span>{wi.assigneeFullName}</span>
                  </>
                ) : (
                  <span className="muted">—</span>
                )}
                {canTakeOver && (
                  <button
                    type="button"
                    className="linkbtn"
                    data-testid={wi.status === 'New' ? 'wi-drawer-takeover-start' : 'wi-drawer-takeover'}
                    onClick={doTakeOver}
                  >{wi.status === 'New' ? 'Take over & Start' : 'Take over'}</button>
                )}
              </div>
            )}
          </div>
          <div className="cell" data-testid="wi-drawer-role-row">
            <div className="l">Role</div>
            {canEdit && (task?.selectableRoles || []).length > 0 ? (
              <select
                data-testid="wi-drawer-role"
                value={wi.roleId ?? ''}
                onChange={(e) => doSaveRole(e.target.value)}
              >
                {!wi.roleId && <option value="">— pick a role —</option>}
                {(task.selectableRoles || []).map((r) => (
                  <option key={r.id} value={r.id}>{r.abbreviation ? `${r.abbreviation} — ${r.roleName}` : r.roleName}</option>
                ))}
              </select>
            ) : (
              <div className="pv" data-testid="wi-drawer-role-ro">{wi.roleName || <span className="muted">—</span>}</div>
            )}
          </div>
        </div>

        {/* UDRAW-S1 §1.3 (+ UDRAW-S3 §2.2) — Toolbelt: commands only
            (Start / Close / + New child work item / Delete). Individual
            fields autosave on blur or via their own inline Save. */}
        {(canAct || canDelete || canHaveChildren) && !isClosed && !createOpen && (
          <div className="wi-drawer-toolbelt" data-testid="wi-drawer-toolbelt">
            {canAct && wi.status === 'New' && (
              <button className="pri" data-testid="wi-drawer-toolbelt-start" onClick={doStart}>▶ Start work</button>
            )}
            {canAct && wi.status === 'Active' && (
              <button className="pri" data-testid="wi-drawer-toolbelt-close" onClick={() => setShowZeroConfirm(true)}>✔ Close work item</button>
            )}
            {canHaveChildren && (
              <button
                type="button"
                data-testid="wi-drawer-new-child"
                onClick={() => { setJustCreated(null); setCreateOpen(true); }}
              >＋ New child work item</button>
            )}
            {canDelete && (
              <>
                <span className="sp" />
                <button className="dngr" data-testid="wi-drawer-delete" onClick={doDelete}>Delete</button>
              </>
            )}
          </div>
        )}

        {/* UDRAW-S3 §2.2 — after a successful child create, banner with
            an "Open →" link to jump into the fresh WI's drawer. */}
        {justCreated && !createOpen && (
          <div
            className="wi-drawer-banner good"
            data-testid="wi-drawer-created-banner"
            style={{ margin: '10px 4px 0' }}
          >
            Created WI-{justCreated.id} · {justCreated.title} —
            {' '}
            <button
              type="button"
              className="linkbtn"
              data-testid="wi-drawer-created-open"
              style={{ background: 'none', border: 'none', padding: 0, color: '#2f6fed', cursor: 'pointer', textDecoration: 'underline' }}
              onClick={() => {
                if (typeof onWorkItemCreated === 'function') {
                  onWorkItemCreated(justCreated, { navigate: true });
                }
              }}
            >Open →</button>
          </div>
        )}

        {showZeroConfirm && (
          <div className="zero-confirm" data-testid="wi-drawer-zero-confirm"
               style={{ margin: '8px 4px', padding: 10, background: '#fffbea',
                        border: '1px solid #e6c34a', borderRadius: 6 }}>
            No hours are logged on this item. Close it with an explicit 0?
            <div style={{ marginTop: 8, display: 'flex', gap: 6 }}>
              <button className="rd-btn primary" data-testid="wi-drawer-zero-yes" onClick={doCloseWith0}>Yes — confirm 0 h</button>
              <button className="rd-btn" onClick={() => setShowZeroConfirm(false)}>Cancel</button>
            </div>
          </div>
        )}

        {/* UDRAW-S3 §2.2 — when the "+ New child work item" button is
            armed, the body swaps to the create form; Cancel returns. */}
        {createOpen && task && (
          <div className="wi-drawer-body-grid single" data-testid="wi-drawer-body">
            <div className="colL">
              <CreateWorkItemForm
                projectId={projectId ?? task?.projectId ?? null}
                task={task}
                parentWi={wi}
                team={team}
                caller={caller}
                isBillable={isBillable}
                onCreated={(dto) => {
                  setCreateOpen(false);
                  setJustCreated({ id: dto?.id, title: dto?.title || '(no title)' });
                  if (typeof onWorkItemCreated === 'function') {
                    onWorkItemCreated(dto, { navigate: false });
                  }
                }}
                onCancel={() => setCreateOpen(false)}
              />
            </div>
          </div>
        )}

        {/* UDRAW-S1 §1.4 — 2-column body. */}
        {!createOpen && (
        <div className="wi-drawer-body-grid" data-testid="wi-drawer-body">
          <div className="colL">
            {parentWiId && (
              <div className="wi-drawer-banner info" data-testid="wi-drawer-parent-banner">
                Parent: WI-{parentWiId}
              </div>
            )}
            {hasChildren && (
              <div className="wi-drawer-banner warn" data-testid="wi-drawer-children-banner">
                This work item has sub-items — hours are logged in the leaves.
              </div>
            )}
            <div>
              <div className="wi-drawer-sec-t">
                Description
                <FieldEditingIndicator fieldKey="deliverable" peer={liveEditing?.deliverable} />
              </div>
              <RichTextField
                testId="wi-drawer-deliverable"
                value={deliverable}
                placeholder={isClosed ? '—' : 'Click to add a description…'}
                readOnly={!canWriteRichText}
                onSave={doSaveDescription}
                onEditingStart={() => live.startEditing({ projectId, workItemId: wi.id, fieldKey: 'deliverable' })}
                onEditingStop={() => live.stopEditing({ projectId, workItemId: wi.id, fieldKey: 'deliverable' })}
              />
            </div>
            <div>
              <div className="wi-drawer-sec-t">
                Acceptance criteria
                <FieldEditingIndicator fieldKey="acceptance" peer={liveEditing?.acceptance} />
              </div>
              <RichTextField
                testId="wi-drawer-accept"
                value={accept}
                placeholder={isClosed ? '—' : 'Click: it is accepted when…'}
                readOnly={!canWriteRichText}
                onSave={doSaveAcceptance}
                onEditingStart={() => live.startEditing({ projectId, workItemId: wi.id, fieldKey: 'acceptance' })}
                onEditingStop={() => live.stopEditing({ projectId, workItemId: wi.id, fieldKey: 'acceptance' })}
              />
            </div>

            {/* UDRAW-S4 §2.2 — Discussion uses the shared component so
                task drawers and WI drawers share list markup + rich
                editor (B/I/U/lists). Kept in the left column.
                UDRAW-S5.1 — editor focus/blur fires fieldEditing so
                peers see the pulsing indicator. */}
            <Discussion
              // UDRAW-S5-A8 / A10 — explicit stable key tied to wi.id
              // (the ANCHOR object), NOT to view reference or comment
              // count. Discussion remounts ONLY when the drawer
              // navigates to a different WI (e.g. via the HIERARCHY
              // tree) — that's the correct time to hand the user a
              // fresh empty editor. A silent A10 refresh reuses the
              // instance, so the DOM contenteditable (and its typed
              // draft) survives untouched.
              key={`wi-${wi.id}-discussion`}
              comments={wi.comments || []}
              onPost={doAddComment}
              testIdPrefix="wi-drawer"
              canPost
              onEditorFocus={() => live.startEditing({ projectId, workItemId: wi.id, fieldKey: 'discussion' })}
              onEditorBlur={() => live.stopEditing({ projectId, workItemId: wi.id, fieldKey: 'discussion' })}
            />
            {liveEditing?.discussion && (
              <FieldEditingIndicator fieldKey="discussion" peer={liveEditing.discussion} />
            )}
          </div>

          <div className="colR">
            <div>
              <div className="wi-drawer-sec-t">Planning</div>
              <div className="row">
                <label>Start</label>
                {canEdit ? (
                  <input
                    type="date"
                    data-testid="wi-drawer-planning-start"
                    value={start}
                    onChange={(e) => { setStart(e.target.value); markDirty('start'); }}
                    onBlur={doSavePlanning}
                    disabled={saving}
                  />
                ) : (
                  <div>{start || '—'}</div>
                )}
              </div>
              <div className="row">
                <label>Finish</label>
                {canEdit ? (
                  <input
                    type="date"
                    data-testid="wi-drawer-planning-finish"
                    value={finish}
                    onChange={(e) => { setFinish(e.target.value); markDirty('finish'); }}
                    onBlur={doSavePlanning}
                    disabled={saving}
                  />
                ) : (
                  <div>{finish || '—'}</div>
                )}
              </div>
            </div>

            {/* QLOG-S2.2 — shared hours block. Same component used by
                quickLogMode below on the resolved auto-holder. Hidden
                for WI-with-children (rollup rendered instead) and for
                Closed (frozen). */}
            <HoursBlock
              wi={wi}
              hasChildren={hasChildren}
              canWrite={canLogHours}
              isAssignee={isAssignee}
              isClosed={isClosed}
              onChanged={onChanged}
            />

            {renderAttachments()}
          </div>
        </div>
        )}

        {/* UDRAW-S1 §3 — sticky-bottom sub-tree of the current WI's phase. */}
        {Array.isArray(treeData) && treeData.length > 0 && (
          <DrawerTaskTree treeData={treeData} onNavigate={onTreeNavigate} />
        )}
      </div>
    </Drawer>
  );
};

export default WorkItemDrawer;

// UDRAW-S2 §2.1 — info drawer now uses the shared TaskDrawerForm shell
// (title / strip / toolbelt / 2-column / sticky sub-tree) for the
// phase / summary / task-with-wis / milestone rows. infoContent for
// those grew to carry `projectId, task, team, caller, treeData,
// UDRAW-S5-A7 — non-blocking notice rendered when the anchoring WI
// or task has vanished from the DeliveryView while the drawer was
// open (a peer deleted the item). The drawer stays open; the viewer
// dismisses it via the header ✕ or the Close button.
function renderMissingDrawer({ open, onClose }) {
  return (
    <Drawer open={open} onClose={onClose} title="Item unavailable" testId="wi-drawer">
      <div style={{ padding: 16 }} data-testid="wi-drawer-missing">
        <div style={{
          background: '#fff8e5', border: '1px solid #f0d78a',
          padding: '10px 12px', borderRadius: 4, color: '#7a5a12',
          fontSize: 13,
        }}>
          This item is no longer available.
        </div>
        <div style={{ marginTop: 12 }}>
          <button
            type="button"
            className="udraw-mini"
            data-testid="wi-drawer-missing-close"
            onClick={onClose}
          >Close</button>
        </div>
      </div>
    </Drawer>
  );
}

// onTreeNavigate, onTaskChanged, mode, banner, childRows, cards`.
// The WI-with-children info drawer (kind: 'wi-with-children') is
// out of scope this slice — it keeps a compact read-only render
// below so the drawer still opens for that legacy path.
function renderInfoDrawer({ open, onClose, infoContent }) {
  const c = infoContent || {};
  if (c.kind === 'wi-with-children') {
    return renderWiWithChildrenInfoDrawer({ open, onClose, infoContent: c });
  }
  return (
    <TaskDrawerForm
      open={open}
      onClose={onClose}
      projectId={c.projectId}
      task={c.task}
      team={c.team}
      caller={c.caller}
      mode={c.mode || 'summary'}
      banner={c.banner || null}
      childRows={c.childRows || null}
      childRowsTitle={c.childRowsTitle || 'Rows'}
      cards={c.cards || null}
      treeData={c.treeData || null}
      onTreeNavigate={c.onTreeNavigate || null}
      onTaskChanged={c.onTaskChanged || null}
      isBillable={c.isBillable !== false}
      onWorkItemCreated={c.onWorkItemCreated || null}
      autoOpenCreate={!!c.autoOpenCreate}
      onCommentAdded={c.onCommentAdded || null}
      onOpenQuickLog={c.onOpenQuickLog || null}
      liveEditing={c.liveEditing || null}
    />
  );
}

// UDRAW-S2 — legacy WI-with-children info render (not in scope). Kept
// as a compact read-only shell so the drawer still opens when a
// rollup WI is clicked; the ADO frame will land here in a later slice.
function renderWiWithChildrenInfoDrawer({ open, onClose, infoContent }) {
  const c = infoContent || {};
  return (
    <Drawer
      open={open}
      onClose={onClose}
      width="lg"
      title={
        <span data-testid="wi-drawer-info-title" className="pdp"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <span style={{ color: '#4a90d9', fontWeight: 600, fontSize: 12 }}>{(c.badge || 'INFO')}</span>
          <span
            data-testid="wi-drawer-info-badge"
            style={{
              fontSize: 10, color: '#0b5394', background: '#e3ecf7',
              border: '1px solid #b7cbe7', borderRadius: 3,
              padding: '1px 7px', letterSpacing: '.4px', fontWeight: 700,
            }}
          >READ ONLY · WI-WITH-CHILDREN</span>
        </span>
      }
      testId="wi-drawer"
    >
      <div className="pdp dlv-drawer" data-testid="wi-drawer-info-wi-with-children" style={{ padding: 12 }}>
        <h3 style={{ margin: '4px 0 6px' }} data-testid="wi-drawer-info-h">{c.title}</h3>
        {c.subtitle && (
          <div style={{ color: '#666', fontSize: 12, marginBottom: 8 }} data-testid="wi-drawer-info-sub">{c.subtitle}</div>
        )}
        {c.banner && (
          <div data-testid="wi-drawer-info-banner"
               style={{ fontSize: 12, color: '#0b5394', background: '#e3ecf7',
                        border: '1px solid #b7cbe7', borderRadius: 3,
                        padding: '6px 10px', margin: '4px 0 12px' }}>
            {c.banner.text}
          </div>
        )}
        {(c.cards || []).length > 0 && (
          <section data-testid="wi-drawer-info-cards" style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
            {c.cards.map((card, i) => (
              <div key={i} data-testid={`wi-drawer-info-card-${i}`}
                   style={{ border: '1px solid #d0d7e2', borderRadius: 4, padding: '8px 12px', minWidth: 120, background: '#fafbfd' }}>
                <div style={{ fontSize: 11, color: '#666' }}>{card.label}</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: card.tone === 'over' ? '#c0392b' : card.tone === 'ok' ? '#2a6b2a' : '#333' }}>
                  {card.value}
                </div>
              </div>
            ))}
          </section>
        )}
        {(c.children || []).length > 0 && (
          <section data-testid="wi-drawer-info-children" style={{ marginTop: 8 }}>
            <h3>{c.childrenTitle || 'Rows'}</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <tbody>
                {c.children.map((ch) => (
                  <tr key={ch.id}
                      data-testid={ch.testId || `wi-drawer-info-child-${ch.id}`}
                      onClick={ch.onClick}
                      style={{ cursor: ch.onClick ? 'pointer' : 'default' }}>
                    <td style={{ padding: '4px 8px', borderBottom: '1px solid #eee' }}>
                      <div>{ch.title}</div>
                      {ch.subtitle && <div style={{ color: '#888', fontSize: 11.5 }}>{ch.subtitle}</div>}
                    </td>
                    <td style={{ padding: '4px 8px', textAlign: 'right', borderBottom: '1px solid #eee', color: '#888', fontSize: 11.5 }}>
                      {ch.trailing || ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}
        <div className="row" style={{ marginTop: 20, borderTop: '1px solid #e0e0e0', paddingTop: 10, justifyContent: 'flex-end' }}>
          <button className="rd-btn" data-testid="wi-drawer-info-close" onClick={onClose}>Close</button>
        </div>
      </div>
    </Drawer>
  );
}

// UDRAW-S2 §2.1 — quick-log inner now wraps the resolver flow around
// the shared TaskDrawerForm shell. The strip's Role cell hosts the
// QLOG-S1 role picker; the right column holds the Продадено /
// Изработено / Остатък cards and the shared HoursBlock; PM/owner can
// still edit Status + Notes and take ownership through the toolbelt
// exactly like in info mode.
function QuickLogInner({ open, onClose, ctx, onChanged }) {
  const projectId = ctx?.projectId;
  const task = ctx?.task || {};
  const roles = task?.selectableRoles || [];
  const [roleId, setRoleId] = useState(roles.length === 1 ? String(roles[0].id) : '');
  const [resolving, setResolving] = useState(false);
  const [wiCtx, setWiCtx] = useState(null); // { wi, task, team, caller } or null
  const [error, setError] = useState('');
  // QLOG-S2.2 — own/task cards refresh from HoursBlock's log responses.
  const [ownHours, setOwnHours] = useState(0);
  const [taskHours, setTaskHours] = useState(0);

  const resolveFor = React.useCallback(async (rid) => {
    if (!projectId || !task?.id) return;
    if (roles.length > 0 && !rid) return;
    setResolving(true);
    setError('');
    try {
      const body = { projectTaskId: Number(task.id), projectRoleId: rid ? Number(rid) : null };
      const res = await quickLogAnchor(projectId, body);
      const wiId = res?.workItemId ?? res?.WorkItemId;
      if (!wiId) { setError('Could not resolve the log-hours holder.'); return; }
      const c = await getWorkItemContext(wiId);
      setWiCtx({ wi: c?.wi, task: c?.task, team: c?.team, caller: c?.caller });
      setOwnHours(Number(c?.wi?.ownHours || 0));
      setTaskHours(Number(c?.wi?.taskLoggedHours || 0));
    } catch (e) {
      setError(readServerError(e));
    } finally {
      setResolving(false);
    }
  }, [projectId, task?.id, roles.length]);

  useEffect(() => {
    if (!open) return;
    // Auto-resolve on mount: single-role tasks resolve immediately;
    // non-billable / no-role tasks also resolve immediately (rid=null).
    if (roles.length <= 1) {
      resolveFor(roles.length === 1 ? String(roles[0].id) : '');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const onRoleChange = (rid) => {
    setRoleId(rid);
    setWiCtx(null);
    if (rid) resolveFor(rid);
  };

  // QLOG-S2.2 — HoursBlock owns the hours UI + write. After each
  // successful log/update/remove call it hands us the fresh DTO so the
  // Own/Task cards above the block re-render with the truth.
  const onLoggedFromBlock = (r) => {
    if (r && typeof r.ownHours === 'number') setOwnHours(r.ownHours);
    if (r && typeof r.taskLoggedHours === 'number') setTaskHours(r.taskLoggedHours);
    if (typeof onChanged === 'function') onChanged();
  };

  const rolePickerNeeded = roles.length > 1;
  const readyToLog = !!wiCtx?.wi?.id && !resolving;
  // QLOG-S2.2 — the holder is self-assigned + Active by construction of
  // the QLOG-S1 resolver; write is always enabled for the caller.
  const holderWi = wiCtx?.wi
    ? { ...wiCtx.wi, ownHours, taskLoggedHours: taskHours }
    : null;

  // UDRAW-S2 §2.1 — Role picker slots into the strip's Role cell so
  // the shell handles state/notes/take-ownership uniformly. Single-role
  // and no-role tasks skip the picker but still resolve on mount.
  const roleCellNode = rolePickerNeeded ? (
    <select
      data-testid="wi-drawer-quicklog-role"
      value={roleId}
      onChange={(e) => onRoleChange(e.target.value)}
    >
      <option value="">— pick a role —</option>
      {roles.map((r) => (
        <option key={r.id} value={r.id}>{r.abbreviation ? `${r.abbreviation} — ${r.roleName}` : r.roleName}</option>
      ))}
    </select>
  ) : null;

  // Budget / done / remaining cards for the right column. Budget comes
  // from the DeliveryTaskDto's BudgetHours; done from the resolver's
  // aggregated taskLoggedHours (fresh after every log).
  const budget = Number(task?.budgetHours || 0);
  const done = Number(taskHours || 0);
  const remaining = Math.max(0, budget - done);
  const cards = [
    { label: 'Sold', value: `${budget.toFixed(1)} h` },
    { label: 'Logged', value: `${done.toFixed(1)} h` },
    { label: 'Remaining', value: `${remaining.toFixed(1)} h`, tone: remaining > 0 ? 'ok' : 'over' },
  ];

  const rightBottomNode = (
    <>
      {resolving && (
        <div
          data-testid="wi-drawer-quicklog-resolving"
          style={{ color: '#666', fontSize: 12 }}
        >Resolving holder…</div>
      )}
      {readyToLog && (
        <HoursBlock
          wi={holderWi}
          hasChildren={false}
          canWrite
          isAssignee
          isClosed={false}
          onLogged={onLoggedFromBlock}
        />
      )}
      {error && (
        <div
          data-testid="wi-drawer-quicklog-err"
          style={{ color: '#c0392b', fontSize: 12, marginTop: 6 }}
        >{error}</div>
      )}
    </>
  );

  return (
    <TaskDrawerForm
      open={open}
      onClose={onClose}
      projectId={projectId}
      task={ctx?.task}
      team={ctx?.team}
      caller={ctx?.caller}
      mode="quickLog"
      banner={{
        tone: 'good',
        text: 'Hours land on your personal Logged work holder under this task — it consumes the sold budget for the chosen role.',
      }}
      cards={cards}
      rightBottomNode={rightBottomNode}
      roleCellNode={roleCellNode}
      treeData={ctx?.treeData || null}
      onTreeNavigate={ctx?.onTreeNavigate || null}
      onTaskChanged={ctx?.onTaskChanged || null}
      isBillable={ctx?.isBillable !== false}
      onWorkItemCreated={ctx?.onWorkItemCreated || null}
      autoOpenCreate={!!ctx?.autoOpenCreate}
      onCommentAdded={ctx?.onCommentAdded || null}
      liveEditing={ctx?.liveEditing || null}
    />
  );
}

// TC-S3 — read-only audit render. Lives in the same file per the
// spec (§3.1 — "не разбивай съществуващия markup на normal режима.
// Ако файлът стане нечетим — изнеси audit-рендера в локална функция
// В СЪЩИЯ файл"). Uses only imported building blocks (no hooks, no
// local state), so it's safe to call from a component early-return.
function renderAuditDrawer({ open, onClose, audit }) {
  const st = audit.status;
  const miClass = st === 'Closed' ? 'st-c' : st === 'Active' ? 'st-i' : 'st-n';
  const miLetter = st === 'Closed' ? 'C' : st === 'Active' ? 'A' : 'N';

  // "Overdue" chip: not Closed AND planned finish is strictly before
  // the local today. Purely visual; audit payload dates are already
  // yyyy-MM-dd local strings so no UTC conversion is needed.
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const isOverdue = st !== 'Closed'
    && audit.plannedFinish
    && (() => {
      const [y, m, d] = audit.plannedFinish.split('-').map(Number);
      return new Date(y, m - 1, d) < today;
    })();

  const dash = (v) => (v == null || v === '' ? '—' : v);
  const fmt = (h) => {
    const n = Number(h) || 0;
    return n % 1 === 0 ? String(n) : n.toFixed(1);
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width="lg"
      title={
        <span data-testid="wi-drawer-audit-title" className="pdp"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <span style={{ color: '#4a90d9', fontWeight: 600, fontSize: 12 }}>WI-{audit.id}</span>
          <span className={`mi ${miClass}`}
                data-testid="wi-drawer-audit-state-pill"
                title={audit.status}
                style={{ display: 'inline-block', width: 18, height: 18, borderRadius: 2,
                         lineHeight: '18px', fontSize: 9, fontWeight: 700, textAlign: 'center' }}>
            {miLetter}
          </span>
          <span
            data-testid="wi-drawer-audit-badge"
            className="ro"
            style={{
              fontSize: 10, color: '#856404', background: '#fff3c2',
              border: '1px solid #e6c34a', borderRadius: 3,
              padding: '1px 7px', letterSpacing: '.4px', fontWeight: 700,
            }}
          >READ ONLY · AUDIT</span>
        </span>
      }
      testId="wi-drawer"
    >
      <div className="pdp dlv-drawer" data-testid="wi-drawer-audit" style={{ padding: 12 }}>
        <h3 style={{ margin: '4px 0 6px' }}>{audit.title}</h3>

        <section data-testid="wi-audit-section-history">
          <h3>History</h3>
          <table className="rd-audit-history" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '4px 8px', borderBottom: '1px solid #e2e2e2' }}>Event</th>
                <th style={{ textAlign: 'left', padding: '4px 8px', borderBottom: '1px solid #e2e2e2' }}>Date</th>
                <th style={{ textAlign: 'left', padding: '4px 8px', borderBottom: '1px solid #e2e2e2' }}>Who</th>
              </tr>
            </thead>
            <tbody>
              <tr data-testid="wi-audit-history-created">
                <td style={{ padding: '4px 8px' }}>Created</td>
                <td style={{ padding: '4px 8px' }}>{dash(audit.createdAt)}</td>
                <td style={{ padding: '4px 8px' }}>{dash(audit.createdByName)}</td>
              </tr>
              <tr data-testid="wi-audit-history-activated">
                <td style={{ padding: '4px 8px' }}>Activated</td>
                <td style={{ padding: '4px 8px' }}>{dash(audit.activatedAt)}</td>
                <td style={{ padding: '4px 8px' }}>{dash(audit.assigneeName)}</td>
              </tr>
              {audit.closedAt && (
                <tr data-testid="wi-audit-history-closed">
                  <td style={{ padding: '4px 8px' }}>Closed</td>
                  <td style={{ padding: '4px 8px' }}>
                    {audit.closedAt} — {fmt(audit.totalHours)} h
                    {audit.closedWithZeroHours && ' (closed with 0 h)'}
                  </td>
                  <td style={{ padding: '4px 8px' }}>{dash(audit.assigneeName)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </section>

        <section data-testid="wi-audit-section-context" style={{ marginTop: 16 }}>
          <h3>Context</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: '3px 12px', fontSize: 12.5 }}>
            <div style={{ color: '#888', fontSize: 11.5 }}>Project</div><div>{audit.projectName}</div>
            <div style={{ color: '#888', fontSize: 11.5 }}>Sold theme</div><div>{audit.taskTitle}</div>
            <div style={{ color: '#888', fontSize: 11.5 }}>Status</div><div>{audit.status}</div>
            <div style={{ color: '#888', fontSize: 11.5 }}>Assignee</div><div>{dash(audit.assigneeName)}</div>
            <div style={{ color: '#888', fontSize: 11.5 }}>Role</div><div data-testid="wi-audit-role">{dash(audit.roleName)}</div>
            <div style={{ color: '#888', fontSize: 11.5 }}>Planned start</div><div>{dash(audit.plannedStart)}</div>
            <div style={{ color: '#888', fontSize: 11.5 }}>Planned finish</div>
            <div>
              {dash(audit.plannedFinish)}
              {isOverdue && (
                <span
                  data-testid="wi-audit-overdue-chip"
                  className="cr-badge"
                  style={{ marginLeft: 8, background: '#fdecea', color: '#c0392b', borderColor: '#e8a9a3' }}
                >overdue</span>
              )}
            </div>
          </div>
        </section>

        <section data-testid="wi-audit-section-deliverable" style={{ marginTop: 16 }}>
          <h3>Deliverable</h3>
          {audit.deliverable
            ? <div style={{ whiteSpace: 'pre-wrap', fontSize: 13 }}>{audit.deliverable}</div>
            : <div style={{ color: '#c0392b', fontStyle: 'italic' }}>— not filled in —</div>}
          <h3 style={{ marginTop: 10 }}>Accept when</h3>
          {audit.acceptanceCriteria
            ? <div style={{ whiteSpace: 'pre-wrap', fontSize: 13 }}>{audit.acceptanceCriteria}</div>
            : <div style={{ color: '#999', fontStyle: 'italic' }}>— not filled in —</div>}
        </section>

        <section data-testid="wi-audit-section-hours" style={{ marginTop: 16 }}>
          <h3>Hours by day · total {fmt(audit.totalHours)} h</h3>
          {(audit.hoursByDay || []).length === 0 ? (
            <div style={{ color: '#999', fontSize: 12 }}>No hours logged against this work item.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '4px 8px', borderBottom: '1px solid #e2e2e2', width: 120 }}>Day</th>
                  <th style={{ textAlign: 'left', padding: '4px 8px', borderBottom: '1px solid #e2e2e2' }}>Who</th>
                  <th style={{ textAlign: 'right', padding: '4px 8px', borderBottom: '1px solid #e2e2e2', width: 80 }}>Hours</th>
                </tr>
              </thead>
              <tbody>
                {audit.hoursByDay.map((row, i) => (
                  <tr key={i} data-testid={`wi-audit-hours-${i}`}>
                    <td style={{ padding: '4px 8px' }}>{row.date}</td>
                    <td style={{ padding: '4px 8px' }}>{row.employeeName}</td>
                    <td style={{ padding: '4px 8px', textAlign: 'right', fontFamily: 'Consolas, monospace' }}>{fmt(row.hours)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        <section data-testid="wi-audit-section-attachments" style={{ marginTop: 16 }}>
          <h3>Attachments</h3>
          {(audit.attachments || []).length === 0 ? (
            <div style={{ color: '#999', fontSize: 12 }}>No files attached.</div>
          ) : (
            <ul style={{ paddingLeft: 20, fontSize: 13 }}>
              {audit.attachments.map((a) => (
                <li key={a.id} data-testid={`wi-audit-att-${a.id}`}>📄 {a.fileName}</li>
              ))}
            </ul>
          )}
        </section>

        <section data-testid="wi-audit-section-discussion" style={{ marginTop: 16 }}>
          <h3>Discussion</h3>
          {(audit.comments || []).length === 0 ? (
            <div style={{ color: '#999', fontSize: 12 }}>No comments.</div>
          ) : (
            (audit.comments || []).map((c, i) => (
              <div key={i} data-testid={`wi-audit-comment-${i}`}
                   style={{ background: '#f8f8fa', border: '1px solid #e0e0e0',
                            padding: '6px 8px', borderRadius: 3, marginBottom: 6 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#444' }}>
                  {c.authorFullName}
                  <span style={{ fontWeight: 400, color: '#888', marginLeft: 6 }}>· {c.createdAt}</span>
                </div>
                <div style={{ marginTop: 3, fontSize: 13, whiteSpace: 'pre-wrap' }}>{c.text}</div>
              </div>
            ))
          )}
        </section>

        <div className="row" style={{ marginTop: 20, borderTop: '1px solid #e0e0e0', paddingTop: 10, justifyContent: 'flex-end' }}>
          <button className="rd-btn" data-testid="wi-drawer-audit-close" onClick={onClose}>Close</button>
        </div>
      </div>
    </Drawer>
  );
}

// QLOG-S2.2 — shared "Log hours" block. Used by both the leaf-WI drawer
// (normal flow) and the quickLog drawer (on the resolved auto-holder)
// so the input control is byte-for-byte the same across drawers.
// Owns: zoom / pickedDay / hoursForDay / monthTotals / showWeekend /
//       dayInput / flashKey / logging state; navigator + dayCard +
//       weekTabs + monthCalendar + stepper; the postHours -> logHours
//       write path with server-driven refresh.
function HoursBlock({ wi, hasChildren = false, canWrite, isAssignee, isClosed, onChanged, onLogged }) {
  const toast = useToast();
  const [zoom, setZoom] = useState('day');
  const [pickedDay, setPickedDay] = useState(() => new Date());
  const [hoursForDay, setHoursForDay] = useState({});
  const [, setMonthTotals] = useState({});
  const [showWeekend, setShowWeekend] = useState(false);
  const [dayInput, setDayInput] = useState('');
  const [flashKey, setFlashKey] = useState(0);
  const [logging, setLogging] = useState(false);

  useEffect(() => {
    setHoursForDay(seedHoursMap(wi?.hoursByDate));
    setMonthTotals({});
    setPickedDay(new Date());
    setZoom('day');
    setShowWeekend(false);
    setDayInput('');
  }, [wi?.id]);

  const hoursByDateKey = wi?.hoursByDate
    ? JSON.stringify(wi.hoursByDate.map((e) => [e?.date, e?.hours]))
    : null;
  useEffect(() => {
    if (!wi || !Array.isArray(wi.hoursByDate)) return;
    setHoursForDay(seedHoursMap(wi.hoursByDate));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wi?.id, hoursByDateKey]);

  const pickedDayIso = pickedDay instanceof Date ? pickedDay.toLocaleDateString('sv-SE') : '';
  const currentDayHours = hoursForDay[pickedDayIso] ?? 0;
  useEffect(() => {
    setDayInput(String(currentDayHours || ''));
  }, [pickedDayIso, currentDayHours]);

  if (!wi) return null;
  const isLeaf = !hasChildren;
  const today = startOfLocalToday();
  const inRange = (d) => isInHoursWindow(d, today);
  const currentDayIso = pickedDayIso;
  const currentHours = currentDayHours;

  const applyServerResult = (r) => {
    if (r?.monthTotals) setMonthTotals(r.monthTotals);
    if (Array.isArray(r?.hoursByDate)) setHoursForDay(seedHoursMap(r.hoursByDate));
  };

  const postHours = async (dateIso, hoursValue) => {
    setLogging(true);
    try {
      const r = await logHours(wi.id, [{ date: dateIso, hours: hoursValue }]);
      applyServerResult(r);
      if (!Array.isArray(r?.hoursByDate)) {
        setHoursForDay((s) => {
          const next = { ...s };
          if (hoursValue === 0) delete next[dateIso];
          else next[dateIso] = hoursValue;
          return next;
        });
      }
      setFlashKey((k) => k + 1);
      if (typeof onLogged === 'function') onLogged(r);
      if (typeof onChanged === 'function') onChanged();
    } catch (err) {
      toast(readServerError(err), { variant: 'error' });
    } finally {
      setLogging(false);
    }
  };

  const renderNavigator = () => {
    const goPrev = () => {
      if (zoom === 'day') setPickedDay((p) => addDays(p, -1));
      else if (zoom === 'week') setPickedDay((p) => addDays(p, -7));
      else setPickedDay((p) => { const n = new Date(p); n.setMonth(n.getMonth() - 1); return n; });
    };
    const goNext = () => {
      if (zoom === 'day') setPickedDay((p) => addDays(p, 1));
      else if (zoom === 'week') setPickedDay((p) => addDays(p, 7));
      else setPickedDay((p) => { const n = new Date(p); n.setMonth(n.getMonth() + 1); return n; });
    };
    const label = zoom === 'day' ? dayLabel(pickedDay)
      : zoom === 'week' ? `Week of ${dayLabel(pickedDay)}`
      : pickedDay.toLocaleDateString(undefined, { year: 'numeric', month: 'long' });
    return (
      <div className="dlv-hnav">
        <button className="rd-btn" onClick={goPrev} data-testid="wi-hours-prev" aria-label="Previous">◀</button>
        <div className="dlv-hnav-zoom">
          {['day', 'week', 'month'].map((z) => (
            <button
              key={z}
              data-testid={`wi-hours-zoom-${z}`}
              className={zoom === z ? 'active' : ''}
              onClick={() => setZoom(z)}>
              {z[0].toUpperCase() + z.slice(1)}
            </button>
          ))}
        </div>
        <button className="rd-btn" onClick={goNext} data-testid="wi-hours-next" aria-label="Next">▶</button>
        <span className="dlv-hnav-label">{label}</span>
      </div>
    );
  };

  const renderDayCard = () => {
    const outOfWindow = !inRange(pickedDay);
    const isToday = isSameDay(pickedDay, today);
    const h = hoursForDay[currentDayIso] || 0;
    return (
      <div className={`dlv-day-card${outOfWindow ? ' out' : ''}${isToday ? ' today' : ''}`}
           data-testid="wi-hours-day-card">
        <div className="dlv-day-card-name">
          {dayLabel(pickedDay)}{isToday ? ' · Today' : ''}
        </div>
        <div className="dlv-day-card-val">
          {h > 0 ? `${h.toFixed(1)} h logged` : 'no entry'}
        </div>
      </div>
    );
  };

  const renderWeekTabs = () => {
    const dow = pickedDay.getDay();
    const mondayOffset = dow === 0 ? -6 : 1 - dow;
    const monday = addDays(pickedDay, mondayOffset);
    const workdays = Array.from({ length: 5 }, (_, i) => addDays(monday, i));
    const weekend = Array.from({ length: 2 }, (_, i) => addDays(monday, 5 + i));
    const renderTab = (d) => {
      const iso = isoOf(d);
      const h = hoursForDay[iso] || 0;
      const active = iso === currentDayIso;
      const out = !inRange(d);
      const isToday = isSameDay(d, today);
      return (
        <button key={iso}
          className={`dlv-week-tab${active ? ' active' : ''}${out ? ' out' : ''}${isToday ? ' today' : ''}`}
          data-testid={`wi-hours-week-tab-${iso}`}
          onClick={() => setPickedDay(new Date(d))}>
          <div className="dow">{d.toLocaleDateString(undefined, { weekday: 'short' })}</div>
          <div className="dnum">{d.getDate()}</div>
          <div className="total">{h > 0 ? h.toFixed(1) : '·'}</div>
        </button>
      );
    };
    return (
      <div className="dlv-week-tabs" data-testid="wi-hours-week-row">
        {workdays.map(renderTab)}
        {showWeekend
          ? weekend.map(renderTab)
          : (
              <button className="dlv-week-tab expander"
                      data-testid="wi-hours-toggle-weekend"
                      onClick={() => setShowWeekend(true)}>+ Sa/Su</button>
            )}
      </div>
    );
  };

  const renderMonthCalendar = () => {
    const first = firstOfMonth(pickedDay);
    const dow = first.getDay();
    const monOffset = dow === 0 ? -6 : 1 - dow;
    const start = addDays(first, monOffset);
    const cells = Array.from({ length: 42 }, (_, i) => addDays(start, i));
    return (
      <div className="dlv-month-cal" data-testid="wi-hours-month-cal">
        {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map((d) => (
          <div key={d} className="dow-h">{d}</div>
        ))}
        {cells.map((d) => {
          const iso = isoOf(d);
          const h = hoursForDay[iso] || 0;
          const inSameMonth = d.getMonth() === pickedDay.getMonth();
          const out = !inRange(d);
          const active = iso === currentDayIso;
          const isToday = isSameDay(d, today);
          return (
            <button key={iso}
              className={`dlv-month-cell${active ? ' active' : ''}${out ? ' out' : ''}${inSameMonth ? '' : ' off'}${isToday ? ' today' : ''}`}
              data-testid={`wi-hours-month-cell-${iso}`}
              onClick={() => setPickedDay(new Date(d))}>
              <div className="dnum">{d.getDate()}</div>
              {h > 0 && (<div className="hval">{h.toFixed(1)}</div>)}
            </button>
          );
        })}
      </div>
    );
  };

  const renderStepper = () => {
    const outOfWindow = !inRange(pickedDay);
    const disabled = logging || !canWrite || outOfWindow;
    const btnLabel = currentHours > 0
      ? `Update ${isSameDay(pickedDay, today) ? 'today' : dayLabel(pickedDay)}`
      : `Log ${isSameDay(pickedDay, today) ? 'today' : dayLabel(pickedDay)}`;
    return (
      <div className="dlv-stepper" data-testid="wi-hours-stepper">
        <div className="dlv-stepper-row">
          <button className="dlv-step-btn minus"
                  data-testid="wi-hours-minus"
                  disabled={disabled}
                  onClick={() => setDayInput((v) => String(clampQuarter((Number(v) || 0) - HOURS_STEP)))}>
            −
          </button>
          <input
            type="number" step="0.25" min="0"
            value={dayInput}
            onChange={(e) => setDayInput(e.target.value)}
            disabled={disabled}
            data-testid="wi-hours-input"
            className="dlv-step-value" />
          <button className="dlv-step-btn plus"
                  data-testid="wi-hours-plus"
                  disabled={disabled}
                  onClick={() => setDayInput((v) => String(clampQuarter((Number(v) || 0) + HOURS_STEP)))}>
            ＋
          </button>
        </div>
        <div className="dlv-preset-row">
          {PRESETS.map((p) => (
            <button key={p}
              className="dlv-preset-btn"
              data-testid={`wi-hours-preset-${p}`}
              disabled={disabled}
              onClick={() => setDayInput(String(p))}>
              {p} h
            </button>
          ))}
        </div>
        <div className="dlv-log-row">
          <button className="dlv-log-btn"
                  data-testid="wi-hours-log"
                  disabled={disabled}
                  onClick={() => postHours(currentDayIso, clampQuarter(Number(dayInput) || 0))}>
            {btnLabel}
          </button>
          {currentHours > 0 && (
            <button className="dlv-remove-btn"
                    data-testid="wi-hours-remove"
                    disabled={disabled}
                    onClick={() => postHours(currentDayIso, 0)}>
              Remove
            </button>
          )}
        </div>
        {!isAssignee && !isClosed && (
          <div className="dlv-log-note" data-testid="wi-hours-assignee-note">
            hours are logged by the assignee — {wi.assigneeFullName || '(no one has taken it over yet)'}
          </div>
        )}
      </div>
    );
  };

  return (
    <div data-testid="wi-drawer-log-hours"
         className={`dlv-log-hours${flashKey % 2 ? ' flash' : ''}`}>
      <h3>Log hours</h3>
      {!isLeaf ? (
        <div className="dlv-parent-note">
          Split across sub-items — hours are logged in the leaves.
          {' Rollup: '}<strong>{(wi.rollupHours || 0).toFixed(1)} h</strong>
        </div>
      ) : (
        <>
          {renderNavigator()}
          {zoom === 'day' && renderDayCard()}
          {zoom === 'week' && renderWeekTabs()}
          {zoom === 'month' && renderMonthCalendar()}
          {renderStepper()}
        </>
      )}
      <div className="dlv-drawer-own-rollup" data-testid="wi-drawer-own-rollup">
        Own: <strong>{(wi.ownHours || 0).toFixed(1)} h</strong>{' · '}
        Σ (with sub-items): <strong>{(wi.rollupHours || 0).toFixed(1)} h</strong>
      </div>
    </div>
  );
}
